import { useBilling } from './useBilling';

/**
 * Composable for common formatting utilities
 */
export function useFormatting() {
    const { hasBillingAccess } = useBilling();

    /**
     * Format a number as currency (USD)
     * Returns placeholder if user doesn't have billing access
     */
    const formatCurrency = (value: number | null | undefined, placeholder: string = '—'): string => {
        if (!hasBillingAccess.value) return placeholder;
        if (value == null) return placeholder;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    /**
     * Format a date in short format (MM/DD/YYYY)
     */
    const formatDate = (value: string | Date | null | undefined, placeholder: string = '—'): string => {
        if (!value) return placeholder;
        const date = typeof value === 'string' ? new Date(value) : value;
        if (isNaN(date.getTime())) return placeholder;
        return date.toLocaleDateString('en-US');
    };

    /**
     * Format a date with time (MM/DD/YYYY HH:MM AM/PM)
     */
    const formatDateTime = (value: string | Date | null | undefined, placeholder: string = '—'): string => {
        if (!value) return placeholder;
        const date = typeof value === 'string' ? new Date(value) : value;
        if (isNaN(date.getTime())) return placeholder;
        return date.toLocaleString('en-US', {
            dateStyle: 'short',
            timeStyle: 'short'
        });
    };

    /**
     * Format a number with commas
     */
    const formatNumber = (value: number | null | undefined, decimals: number = 0, placeholder: string = '—'): string => {
        if (value == null) return placeholder;
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
    };

    /**
     * Format hours (e.g., 2.5 -> "2.5 hrs")
     */
    const formatHours = (value: number | null | undefined, placeholder: string = '—'): string => {
        if (value == null) return placeholder;
        const formatted = formatNumber(value, 2);
        return `${formatted} hrs`;
    };

    /**
     * Format a phone number
     */
    const formatPhone = (value: string | null | undefined, placeholder: string = '—'): string => {
        if (!value) return placeholder;
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        if (cleaned.length === 11 && cleaned[0] === '1') {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        }
        return value;
    };

    /**
     * Truncate text with ellipsis
     */
    const truncate = (value: string | null | undefined, maxLength: number, placeholder: string = ''): string => {
        if (!value) return placeholder;
        if (value.length <= maxLength) return value;
        return value.slice(0, maxLength - 3) + '...';
    };

    return {
        formatCurrency,
        formatDate,
        formatDateTime,
        formatNumber,
        formatHours,
        formatPhone,
        truncate
    };
}

export default useFormatting;
